import { useEffect, useMemo, useState } from 'react';
import {
  Field,
  Input,
  Text,
  tokens,
} from '@fluentui/react-components';
import { CheckmarkCircleFilled, DismissCircleFilled } from '@fluentui/react-icons';
import type { Person } from '../../../api/src/shared/contract';
import { findByEmail } from '../../lib/directory';
import { usePaneStyles } from '../styles';
import { LabeledField } from './LabeledField';

interface RequesterFieldProps {
  people: Person[];
  value: string;
  onChange: (email: string) => void;
}

export function RequesterField({ people, value, onChange }: RequesterFieldProps) {
  const styles = usePaneStyles();
  const match = useMemo(() => findByEmail(people, value), [people, value]);
  const dirty = value.trim().length > 0;

  const hint = match
    ? [
        `Matched to ${match.name}`,
        match.site,
        match.department,
      ]
        .filter(Boolean)
        .join(' · ')
    : dirty
      ? 'No SolarWinds user with that exact email.'
      : 'Defaults to the email sender. Must resolve before the ticket can be created.';

  const adornment = dirty ? (
    match ? (
      <CheckmarkCircleFilled primaryFill={tokens.colorPaletteGreenForeground1} />
    ) : (
      <DismissCircleFilled primaryFill={tokens.colorPaletteRedForeground1} />
    )
  ) : null;

  return (
    <Field
      label="Requester"
      required
      validationState={dirty ? (match ? 'success' : 'error') : 'none'}
    >
      <Input
        value={value}
        onChange={(_, data) => onChange(data.value)}
        {...(adornment ? { contentAfter: adornment } : {})}
      />
      <Text className={match ? styles.hint : dirty ? styles.hintWarn : styles.hint}>{hint}</Text>
    </Field>
  );
}

interface CcListProps {
  recipients: { name: string; email: string }[];
  people: Person[];
  selected: Set<string>;
  onToggle: (email: string) => void;
}

export function CcList({ recipients, people, selected, onToggle }: CcListProps) {
  const styles = usePaneStyles();

  if (recipients.length === 0) {
    return (
      <LabeledField label="CC">
        <Text className={styles.hint}>No CC recipients on this message.</Text>
      </LabeledField>
    );
  }

  const knownSelected = recipients.filter((r) => {
    const person = findByEmail(people, r.email);
    return person && selected.has(person.email.toLowerCase());
  }).length;

  return (
    <LabeledField label="CC" meta={`${knownSelected} of ${recipients.length} selected`}>
      <div className={styles.rows}>
        {recipients.map((r, index) => {
          const person = findByEmail(people, r.email);
          const key = (person?.email ?? r.email).toLowerCase();
          const known = Boolean(person);
          const checked = known && selected.has(key);
          const isLast = index === recipients.length - 1;
          return (
            <label
              key={`${r.email}-${index}`}
              className={[
                styles.row,
                isLast ? styles.rowLast : '',
                known ? '' : styles.rowDisabled,
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={!known}
                onChange={() => {
                  if (known) onToggle(key);
                }}
                style={{ flexShrink: 0 }}
              />
              <span className={styles.who}>
                <span className={styles.whoName}>{person?.name || r.name || r.email}</span>
                <span className={styles.whoEmail}>
                  {known ? r.email : 'Not a SolarWinds user'}
                </span>
              </span>
              {known ? (
                <CheckmarkCircleFilled primaryFill={tokens.colorPaletteGreenForeground1} />
              ) : (
                <DismissCircleFilled primaryFill={tokens.colorPaletteRedForeground1} />
              )}
            </label>
          );
        })}
      </div>
    </LabeledField>
  );
}

/** Debounces an email value so we don't re-filter on every keystroke for large directories. */
export function useDebounced(value: string, ms = 150): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}
